<script lang="ts">
  import { onMount } from 'svelte';
  import { createQuery } from '@tanstack/svelte-query';
  import Button from '../ui/Button.svelte';
  import Card from '../ui/Card.svelte';
  import InlineMessage from '../ui/InlineMessage.svelte';
  import type { SessionArchivedResult } from '../session/room-session-state';
  import { QueryHttpError } from '../api/http';
  import { archiveQueryOptions } from '../queries/results';
  import PostMatch from '../results/PostMatch.svelte';

  /**
   * Standalone archive route: owns query loading/auth/retry and renders the
   * shared post-match body with settled playback. Establishes no room socket.
   */
  interface Props {
    roundId: string;
    result?: SessionArchivedResult | null;
    credential: string | null;
    profileGeneration: number;
    getProfileCredential: () => Promise<string>;
    rejectProfileCredential: (credential: string) => void;
    openLeaderboard: () => void;
  }

  let {
    roundId,
    result: suppliedResult = null,
    credential,
    profileGeneration,
    getProfileCredential,
    rejectProfileCredential,
    openLeaderboard,
  }: Props = $props();
  let credentialError = $state('');
  let gettingCredential = $state(false);
  let rejectedError: Error | null = null;
  const query = createQuery(() =>
    archiveQueryOptions(roundId, credential, profileGeneration, !suppliedResult),
  );
  const result = $derived(suppliedResult ?? query.data ?? null);
  const error = $derived(credentialError || query.error?.message || '');
  const loading = $derived(
    !result && (gettingCredential || (Boolean(credential) && (query.isPending || query.isFetching))),
  );

  function ensureCredential(): void {
    if (gettingCredential) return;
    gettingCredential = true;
    credentialError = '';
    void getProfileCredential()
      .catch((cause) => {
        credentialError = cause instanceof Error ? cause.message : 'Result unavailable.';
      })
      .finally(() => {
        gettingCredential = false;
      });
  }

  function retry(): void {
    credentialError = '';
    if (!credential) {
      ensureCredential();
      return;
    }
    void query.refetch();
  }

  $effect(() => {
    const queryError = query.error;
    if (
      queryError instanceof QueryHttpError &&
      queryError.status === 401 &&
      queryError !== rejectedError &&
      credential
    ) {
      rejectedError = queryError;
      credentialError = queryError.message;
      rejectProfileCredential(credential);
    }
  });

  onMount(() => {
    if (!suppliedResult && !credential) ensureCredential();
  });
</script>

<section id="screen-archive" class="screen">
  {#if result}
    {#key result.roundId}
      <PostMatch
        results={{
          round: result.round,
          roundId: result.roundId,
          config: result.config,
          players: result.players,
          missed: result.missed ?? [],
          possible: result.possible,
          found: result.found,
          board: result.board,
          startedAt: result.startedAt,
          endedAt: result.endedAt,
        }}
        mode={result.mode}
        meId={result.viewerId}
        playback={{ kind: 'settled' }}
        dailyStanding={result.dailyStanding ?? null}
        leaderboardLabel={result.mode === 'daily' ? 'Leaderboard' : null}
        onLeaderboard={result.mode === 'daily' ? openLeaderboard : null}
      />
    {/key}
  {:else}
    <Card>
      <h2>Archived result</h2>
      {#if loading}<InlineMessage message="Loading result…" />{/if}
      <InlineMessage message={error} intent="error" />
      {#if error || loading}
        <Button variant="ghost" onclick={retry} disabled={loading} aria-busy={loading}
          >{loading ? 'Loading…' : 'Retry'}</Button
        >
      {/if}
    </Card>
  {/if}
</section>
